import React, { useState, useMemo } from 'react';
import { format, parseISO, addHours, compareAsc, isAfter, startOfDay, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import { CalendarIcon, Clock, Building2, Euro, StickyNote, Search, X, Users, Filter, Download, CheckSquare2 } from 'lucide-react';
import type { Room, Booking, BookingStatus } from './BookingCalendar';
import { EditBookingDialog } from './EditBookingDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RevenueDashboard } from './RevenueDashboard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatCurrency } from '@/lib/utils';
import { staffConfig } from '@/config/staff';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { BatchEditBookingDialog } from './BatchEditBookingDialog';
import { useDevice } from '@/hooks/use-device';

const filteredStaffConfig = staffConfig.filter(staff => staff.name !== 'SuHe');

type CurrentBookingsProps = {
  rooms: Room[];
  bookings: Booking[];
  onBookingUpdate: (booking: Booking) => void;
  onBookingDelete: (bookingId: number) => void;
};

const statusColors = {
  paid: 'bg-blue-100 border-l-4 border-blue-500 text-blue-800',
  booked: 'bg-emerald-100 border-l-4 border-emerald-500 text-emerald-800',
  pending: 'bg-amber-100 border-l-4 border-amber-500 text-amber-800'
};

const statusLabels = {
  paid: 'Paid',
  booked: 'Booked',
  pending: 'Pending'
};

const filterBarStyles = {
  wrapper: "flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors duration-200",
  icon: "h-4 w-4",
  select: "w-[200px] bg-white transition-colors duration-200",
  input: "bg-white transition-colors duration-200",
  button: "transition-all duration-200"
};

const filterVariants = {
  base: {
    wrapper: "bg-blue-50/50 border-blue-100 hover:bg-blue-50 hover:border-blue-200",
    icon: "text-blue-500",
    select: "border-blue-200 focus:ring-blue-500 focus:border-blue-300",
    input: "border-blue-200 focus:ring-blue-500 focus:border-blue-300 pl-10 pr-4"
  },
  export: {
    button: "text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
  },
  clear: {
    button: "text-gray-600 hover:text-gray-900 hover:bg-gray-50 active:bg-gray-100"
  }
};

const statusStyles = {
  paid: "bg-blue-50 text-blue-700 border-blue-200",
  booked: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200"
};

// Helper function to safely parse date strings
const safeParseISO = (dateStr: string, timeStr: string): Date | null => {
  try {
    // Check if the date string already contains a time component (T)
    const cleanDateStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const dateTimeStr = `${cleanDateStr}T${timeStr}`;
    
    const parsedDate = parseISO(dateTimeStr);
    if (!isValid(parsedDate)) {
      console.warn(`Invalid date after parsing: ${dateTimeStr}`);
      return null;
    }
    return parsedDate;
  } catch (error) {
    console.error(`Error parsing date: ${dateStr} with time ${timeStr}`, error);
    return null;
  }
};

// Helper function to safely format dates
const safeFormatDate = (date: Date | null, formatStr: string, fallback: string = 'Invalid date'): string => {
  if (!date || !isValid(date)) return fallback;
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error(`Error formatting date: ${date}`, error);
    return fallback;
  }
};

export function CurrentBookings({ 
  rooms, 
  bookings,
  onBookingUpdate,
  onBookingDelete
}: CurrentBookingsProps) {
  const { toast } = useToast();
  const { isMobile } = useDevice();
  const [selectedBooking, setSelectedBooking] = useState<{
    booking: Booking;
    room: Room;
  } | null>(null);
  const [selectedBookings, setSelectedBookings] = useState<Set<number>>(new Set());
  const [showBatchEdit, setShowBatchEdit] = useState(false);
  const [clientFilter, setClientFilter] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedStaff, setSelectedStaff] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<BookingStatus | 'all'>('all');

  const today = startOfDay(new Date());

  // Debug booking data
  React.useEffect(() => {
    console.log(`Total bookings: ${bookings.length}`);
    
    // Check for bookings with invalid dates
    const invalidDates = bookings.filter(b => {
      const date = safeParseISO(b.date, b.timeSlot);
      return !date;
    });
    
    if (invalidDates.length > 0) {
      console.warn(`Found ${invalidDates.length} bookings with invalid dates`);
      invalidDates.forEach(b => {
        console.warn(`Invalid booking: id=${b.id}, date=${b.date}, timeSlot=${b.timeSlot}`);
      });
    }
  }, [bookings]);

  // Deduplicate client names to prevent duplicate entries in dropdown
  const clientNames = useMemo(() => {
    const names = new Set<string>();
    bookings.forEach(booking => {
      if (booking.bookedBy !== 'SuHe' && booking.clientName) {
        names.add(booking.clientName);
      }
    });
    return Array.from(names).sort();
  }, [bookings]);

  // Filter bookings with improved deduplication
  const filteredBookings = useMemo(() => {
    // First create a map using booking IDs as keys to ensure uniqueness
    const bookingsMap = new Map();
    
    // Debug counters
    let totalProcessed = 0;
    let skippedInvalid = 0;
    let skippedFilters = 0;
    
    bookings.forEach(booking => {
      totalProcessed++;
      
      if (booking.bookedBy === 'SuHe') {
        skippedFilters++;
        return; // Skip SuHe bookings
      }
      
      // Validate date and time before processing
      if (!booking.date || !booking.timeSlot) {
        skippedInvalid++;
        return;
      }
      
      // Apply filters
      if (selectedStaff !== 'all' && booking.bookedBy !== selectedStaff) {
        skippedFilters++;
        return;
      }

      if (selectedStatus !== 'all' && booking.status !== selectedStatus) {
        skippedFilters++;
        return;
      }

      const searchTerm = selectedClient || clientFilter;
      if (searchTerm && !booking.clientName?.toLowerCase().includes(searchTerm.toLowerCase())) {
        skippedFilters++;
        return;
      }
      
      // Store in map using ID as key to prevent duplicates
      bookingsMap.set(booking.id, booking);
    });
    
    const result = Array.from(bookingsMap.values());
    console.log(`Filtered bookings: ${result.length} (processed: ${totalProcessed}, skipped invalid: ${skippedInvalid}, skipped by filters: ${skippedFilters})`);
    
    return result;
  }, [bookings, selectedStaff, selectedStatus, clientFilter, selectedClient]);

  const { pastBookings, upcomingBookings } = useMemo(() => {
    // Debug counters
    let validDates = 0;
    let invalidDates = 0;
    
    const result = filteredBookings.reduce((acc, booking) => {
      // Safely parse the date
      const startTime = safeParseISO(booking.date, booking.timeSlot);
      if (!startTime) {
        invalidDates++;
        // Skip invalid dates
        return acc;
      }
      
      validDates++;
      const bookingEndTime = addHours(startTime, booking.duration);
      
      if (isAfter(bookingEndTime, today)) {
        acc.upcomingBookings.push(booking);
      } else {
        acc.pastBookings.push(booking);
      }
      return acc;
    }, {
      pastBookings: [] as Booking[],
      upcomingBookings: [] as Booking[]
    });
    
    console.log(`Date parsing: valid=${validDates}, invalid=${invalidDates}`);
    console.log(`Upcoming bookings: ${result.upcomingBookings.length}, Past bookings: ${result.pastBookings.length}`);
    
    return result;
  }, [filteredBookings, today]);

  const sortedPastBookings = useMemo(() => {
    return [...pastBookings].sort((a, b) => {
      const dateA = safeParseISO(a.date, a.timeSlot);
      const dateB = safeParseISO(b.date, b.timeSlot);
      
      if (!dateA || !dateB) return 0;
      return compareAsc(dateB, dateA);
    });
  }, [pastBookings]);

  const sortedUpcomingBookings = useMemo(() => {
    return [...upcomingBookings].sort((a, b) => {
      const dateA = safeParseISO(a.date, a.timeSlot);
      const dateB = safeParseISO(b.date, b.timeSlot);
      
      if (!dateA || !dateB) return 0;
      
      const dateDiff = compareAsc(dateA, dateB);
      
      if (dateDiff !== 0) return dateDiff;
      
      const statusPriority = { paid: 0, booked: 1, pending: 2 };
      return statusPriority[a.status] - statusPriority[b.status];
    });
  }, [upcomingBookings]);

  const getRoomById = (roomId: number) => {
    return rooms.find(room => room.id === roomId);
  };

  const handleBookingClick = (booking: Booking) => {
    const room = getRoomById(booking.roomId);
    if (room) {
      setSelectedBooking({ booking, room });
    }
  };

  const handleClientSelect = (value: string) => {
    setSelectedClient(value);
    setClientFilter('');
  };

  const handleClientSearch = (value: string) => {
    setClientFilter(value);
    setSelectedClient('');
  };

  const clearFilters = () => {
    setClientFilter('');
    setSelectedClient('');
    setSelectedStaff('all');
    setSelectedStatus('all');
  };

  const handleBookingSelect = (bookingId: number, checked: boolean) => {
    setSelectedBookings(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(bookingId);
      } else {
        newSet.delete(bookingId);
      }
      return newSet;
    });
  };

  const handleBatchEdit = (updates: Partial<Booking>) => {
    const selectedBookingsList = filteredBookings.filter(b => selectedBookings.has(b.id));
    selectedBookingsList.forEach(booking => {
      onBookingUpdate({
        ...booking,
        ...updates
      });
    });

    setSelectedBookings(new Set());
    setShowBatchEdit(false);

    toast({
      title: "Batch Update Complete",
      description: `Updated ${selectedBookingsList.length} bookings`,
    });
  };

  const handleBatchDelete = (bookingIds: number[]) => {
    bookingIds.forEach(onBookingDelete);
    setSelectedBookings(new Set());
    setShowBatchEdit(false);

    toast({
      title: "Batch Delete Complete",
      description: `Deleted ${bookingIds.length} bookings`,
    });
  };

  const handleExport = () => {
    const allBookings = [...sortedUpcomingBookings, ...sortedPastBookings];
    
    const headers = [
      'Date',
      'Time',
      'Duration',
      'Room',
      'Client',
      'Booked By',
      'Status',
      'Amount',
      'Notes'
    ];

    const rows = allBookings.map(booking => {
      const room = getRoomById(booking.roomId);
      const startTime = safeParseISO(booking.date, booking.timeSlot);
      const endTime = startTime ? addHours(startTime, booking.duration) : null;

      return [
        startTime ? safeFormatDate(startTime, 'dd/MM/yyyy', 'Invalid date') : 'Invalid date',
        startTime && endTime 
          ? `${safeFormatDate(startTime, 'HH:mm')} - ${safeFormatDate(endTime, 'HH:mm')}` 
          : 'Invalid time',
        `${booking.duration}h`,
        room?.name || '',
        booking.clientName,
        booking.bookedBy,
        statusLabels[booking.status],
        formatCurrency(booking.revenue?.amount || 0).replace('€', ''),
        booking.revenue?.notes || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        row.map(cell => 
          typeof cell === 'string' && cell.includes(',') 
            ? `"${cell.replace(/"/g, '""')}"` 
            : cell
        ).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `bookings_export_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Export Complete",
      description: `${allBookings.length} bookings exported to CSV`,
    });
  };

  const renderBookingCard = (booking: Booking) => {
    // Safely parse dates, providing fallbacks for invalid values
    const startTime = safeParseISO(booking.date, booking.timeSlot);
    if (!startTime) {
      // Skip rendering this booking if date is invalid
      console.warn(`Skipping invalid booking date: ${booking.date}T${booking.timeSlot} for booking ID ${booking.id}`);
      return null;
    }
    
    const endTime = addHours(startTime, booking.duration);
    const room = getRoomById(booking.roomId);
    const staff = filteredStaffConfig.find(s => s.name === booking.bookedBy);
    
    if (isMobile) {
      return (
        <div
          key={booking.id}
          className={cn(
            "rounded-lg shadow-sm relative",
            statusColors[booking.status]
          )}
          onClick={() => handleBookingClick(booking)}
        >
          <div className="flex items-start p-3">
            {/* Main Content */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">{booking.clientName}</span>
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-full",
                  staff?.color || ''
                )}>
                  {booking.bookedBy}
                </span>
              </div>

              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  <span>{room?.name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  <span>{safeFormatDate(startTime, 'EEE, MMM d')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  <span>
                    {safeFormatDate(startTime, 'HH:mm')} - {safeFormatDate(endTime, 'HH:mm')}
                    <span className="ml-1 text-gray-500">({booking.duration}h)</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Checkbox - Moved to the right side */}
            <Checkbox
              checked={selectedBookings.has(booking.id)}
              onCheckedChange={(checked) => {
                handleBookingSelect(booking.id, checked as boolean);
              }}
              className={cn(
                "h-5 w-5 ml-2 mt-1",
                "transition-all duration-200",
                "data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500",
                "border-2 border-gray-300",
                "hover:border-blue-400",
                selectedBookings.has(booking.id) 
                  ? "bg-blue-500 border-blue-500" 
                  : "bg-white hover:bg-blue-50/50"
              )}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

            {booking.revenue && (
              <div className="mt-2 pt-2 border-t border-gray-200/50">
                <div className="flex items-center gap-1.5 text-xs">
                  <Euro className="h-3.5 w-3.5" />
                  <span className="font-medium">{formatCurrency(booking.revenue.amount)}</span>
                </div>
                {booking.revenue.hasNotes && booking.revenue.notes && (
                  <div className="mt-1.5 text-xs flex items-start gap-1.5">
                    <StickyNote className="h-3.5 w-3.5 mt-0.5" />
                    <span className="text-gray-600 line-clamp-2">
                      {booking.revenue.notes}
                    </span>
                  </div>
                )}
              </div>
            )}
        </div>
      );
    }

    return (
      <div
        key={booking.id}
        className={cn(
          "p-4 rounded-lg shadow-sm transition-shadow relative group",
          statusColors[booking.status],
          "hover:shadow-md"
        )}
      >
        <div className="absolute right-0 top-0 p-4">
          <div className="w-5 h-5 relative">
            <Checkbox
              checked={selectedBookings.has(booking.id)}
              onCheckedChange={(checked) => handleBookingSelect(booking.id, checked as boolean)}
              className={cn(
                "absolute inset-0 w-5 h-5",
                "transition-all duration-200",
                "data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500",
                "border-2 border-gray-300",
                "hover:border-blue-400",
                "focus:ring-2 focus:ring-blue-100",
                selectedBookings.has(booking.id) 
                  ? "bg-blue-500 border-blue-500" 
                  : "bg-white hover:bg-blue-50/50"
              )}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        <div 
          className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-8 cursor-pointer"
          onClick={() => handleBookingClick(booking)}
        >
          <div className="space-y-2">
            <div className="text-lg font-semibold">
              {booking.clientName}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4" />
              <span>{room?.name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CalendarIcon className="h-4 w-4" />
              <span>{safeFormatDate(startTime, 'EEEE, MMMM d, yyyy')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4" />
              <span>
                {safeFormatDate(startTime, 'h:mm a')} - {safeFormatDate(endTime, 'h:mm a')} ({booking.duration}h)
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium">Booked by: </span>
              <span className={cn("px-2 py-1 rounded-md", staff?.color || '')}>
                {booking.bookedBy}
              </span>
            </div>
            <div className="text-sm">
              <span className="font-medium">Status: </span>
              {statusLabels[booking.status]}
            </div>
            {booking.revenue && (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <Euro className="h-4 w-4" />
                  <span className="font-medium">{formatCurrency(booking.revenue.amount)}</span>
                </div>
                {booking.revenue.hasNotes && booking.revenue.notes && (
                  <div className="flex items-start gap-2 text-sm mt-3 bg-white/40 p-3 rounded-lg">
                    <StickyNote className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium mb-1">Notes:</div>
                      <div className="text-gray-700 bg-white/50 p-2 rounded whitespace-pre-wrap break-words max-h-24 overflow-y-auto">
                        {booking.revenue.notes}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title: string, bookings: Booking[], emptyMessage: string) => {
    // Filter out null elements after rendering
    const renderedBookings = bookings
      .map(booking => ({
        id: booking.id,
        element: renderBookingCard(booking)
      }))
      .filter(item => item.element !== null);
    
    console.log(`Rendered ${renderedBookings.length} of ${bookings.length} bookings in section: ${title}`);
    
    return (
      <div className="space-y-4">
        <h3 className={cn(
          "font-semibold text-gray-700",
          isMobile ? "text-base" : "text-lg"
        )}>{title} ({renderedBookings.length})</h3>
        {renderedBookings.length === 0 ? (
          <div className="text-center text-gray-500 py-8 bg-gray-50/50 rounded-lg">
            {emptyMessage}
          </div>
        ) : (
          <div className={cn(
            isMobile ? "space-y-2" : "space-y-4"
          )}>
            {renderedBookings.map(item => (
              <React.Fragment key={item.id}>
                {item.element}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className={cn(
          "flex items-center gap-4",
          isMobile && "flex-col items-start gap-2 w-full"
        )}>
          <h2 className={cn(
            "text-2xl font-bold text-gray-900",
            isMobile && "text-xl"
          )}>
            Bookings Overview
          </h2>
          {selectedBookings.size > 0 && (
            <div className={cn(
              "flex items-center gap-2",
              isMobile && "w-full flex-col"
            )}>
              <Button
                variant="outline"
                onClick={() => setShowBatchEdit(true)}
                className={cn(
                  "bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 hover:text-blue-700 hover:border-blue-300",
                  isMobile && "w-full justify-center"
                )}
              >
                Edit {selectedBookings.size} Selected
              </Button>
              <Button
                variant="ghost"
                onClick={() => setSelectedBookings(new Set())}
                className={cn(
                  "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                  isMobile && "w-full justify-center"
                )}
              >
                Clear Selection
              </Button>
            </div>
          )}
        </div>

        {!isMobile && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExport}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        )}
      </div>

      <div className={cn(
        "flex items-center gap-4 mb-6 bg-white p-4 rounded-lg border shadow-sm",
        isMobile && "flex-col items-stretch"
      )}>
        {/* 1. Multiselect Button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            const allBookingIds = new Set(filteredBookings.map(b => b.id));
            setSelectedBookings(allBookingIds);
          }}
          className={cn(
            "h-9 w-9",
            filterVariants.base.wrapper,
            "text-blue-600 hover:text-blue-700"
          )}
          title={`Select All (${filteredBookings.length})`}
        >
          <CheckSquare2 className="h-5 w-5" />
        </Button>

        {/* 2. Status Select */}
        <div className={cn(filterBarStyles.wrapper, filterVariants.base.wrapper)}>
          <Filter className={cn(filterBarStyles.icon, filterVariants.base.icon)} />
          <Select
            value={selectedStatus}
            onValueChange={(value) => setSelectedStatus(value as BookingStatus | 'all')}
          >
            <SelectTrigger className={cn(filterBarStyles.select, filterVariants.base.select)}>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="paid">
                <div className={cn("px-2 py-1 rounded-md", statusStyles.paid)}>
                  {statusLabels.paid}
                </div>
              </SelectItem>
              <SelectItem value="booked">
                <div className={cn("px-2 py-1 rounded-md", statusStyles.booked)}>
                  {statusLabels.booked}
                </div>
              </SelectItem>
              <SelectItem value="pending">
                <div className={cn("px-2 py-1 rounded-md", statusStyles.pending)}>
                  {statusLabels.pending}
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* 3. Staff Selection */}
        <div className={cn(filterBarStyles.wrapper, filterVariants.base.wrapper)}>
          <Users className={cn(filterBarStyles.icon, filterVariants.base.icon)} />
          <Select
            value={selectedStaff}
            onValueChange={setSelectedStaff}
          >
            <SelectTrigger className={cn(filterBarStyles.select, filterVariants.base.select)}>
              <SelectValue placeholder="Filter by staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {filteredStaffConfig.map(staff => (
                <SelectItem key={staff.id} value={staff.name}>
                  <span className={cn("px-2 py-1 rounded-md", staff.color)}>
                    {staff.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 4. Client Select */}
        <div className={cn(filterBarStyles.wrapper, filterVariants.base.wrapper)}>
          <Users className={cn(filterBarStyles.icon, filterVariants.base.icon)} />
          <Select
            value={selectedClient}
            onValueChange={handleClientSelect}
          >
            <SelectTrigger className={cn(filterBarStyles.select, filterVariants.base.select)}>
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              {clientNames.map(name => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 5. Search Client */}
        <div className={cn(filterBarStyles.wrapper, filterVariants.base.wrapper, "relative flex-1")}>
          <Search className={cn(
            filterBarStyles.icon, 
            filterVariants.base.icon,
            "absolute left-6 top-1/2 -translate-y-1/2"
          )} />
          <Input
            type="text"
            placeholder="Search by client name..."
            value={clientFilter}
            onChange={(e) => handleClientSearch(e.target.value)}
            className={cn(filterBarStyles.input, filterVariants.base.input)}
          />
        </div>

        {/* Clear Filters Button */}
        {(clientFilter || selectedClient || selectedStaff !== 'all' || selectedStatus !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className={cn(
              filterBarStyles.button,
              filterVariants.clear.button,
              "flex items-center gap-2"
            )}
          >
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        )}
      </div>

      {(selectedStaff !== 'all' || selectedStatus !== 'all' || clientFilter || selectedClient) && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-500">Active Filters:</span>
          <div className="flex items-center gap-2">
            {selectedStaff !== 'all' && (
              <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                Staff: {selectedStaff}
              </Badge>
            )}
            {selectedStatus !== 'all' && (
              <Badge variant="secondary" className={cn(statusStyles[selectedStatus])}>
                Status: {statusLabels[selectedStatus]}
              </Badge>
            )}
            {(clientFilter || selectedClient) && (
              <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                Client: {clientFilter || selectedClient}
              </Badge>
            )}
          </div>
        </div>
      )}

      <div className={cn(
        "grid gap-6",
        isMobile ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"
      )}>
        <ScrollArea className={cn(
          "rounded-lg border shadow-sm bg-white",
          isMobile ? "h-[calc(100vh-8rem)]" : "h-[calc(100vh-12rem)]"
        )}>
          <div className="p-6 space-y-8">
            {renderSection(
              "Upcoming Bookings",
              sortedUpcomingBookings,
              selectedStaff !== 'all'
                ? `No upcoming bookings for ${selectedStaff}`
                : "No upcoming bookings scheduled"
            )}

            <div className="border-t pt-8">
              {renderSection(
                "Past Bookings",
                sortedPastBookings,
                selectedStaff !== 'all'
                  ? `No past bookings for ${selectedStaff}`
                  : "No past bookings found"
              )}
            </div>
          </div>
        </ScrollArea>

        {!isMobile && (
          <ScrollArea className="h-[calc(100vh-12rem)] rounded-lg border shadow-sm bg-white">
            <div className="p-6">
              <RevenueDashboard bookings={filteredBookings} />
            </div>
          </ScrollArea>
        )}
      </div>

      {isMobile && (
        <div className="fixed bottom-4 right-4">
          <Button
            variant="outline"
            size="icon"
            onClick={handleExport}
            className="h-12 w-12 rounded-full bg-white shadow-lg border-2"
          >
            <Download className="h-5 w-5" />
          </Button>
        </div>
      )}

      {selectedBooking && (
        <EditBookingDialog
          booking={selectedBooking.booking}
          room={selectedBooking.room}
          onClose={() => setSelectedBooking(null)}
          onUpdate={onBookingUpdate}
          onDelete={onBookingDelete}
        />
      )}

      {showBatchEdit && (
        <BatchEditBookingDialog
          bookings={filteredBookings.filter(b => selectedBookings.has(b.id))}
          onClose={() => setShowBatchEdit(false)}
          onUpdate={handleBatchEdit}
          onDelete={handleBatchDelete}
        />
      )}
    </div>
  );
}